import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { loadActivities, loadPrintables, loadToys, loadBooks, type Activity, type Printable, type Toy, type Book } from '@/services/content'
import styles from './Library.module.css'
import Icon from '@/components/ui/Icon'

const categories = [
  { id: 'all', label: 'All' },
  { id: 'proprioceptive', label: 'Movement' },
  { id: 'tactile', label: 'Touch' },
  { id: 'vestibular', label: 'Balance' },
  { id: 'auditory', label: 'Sound' },
  { id: 'visual', label: 'Visual' },
]

const timeFilters = [
  { id: 'all', label: 'Any time' },
  { id: '5', label: '5 min or less' },
  { id: '10', label: '10 min or less' },
  { id: '15', label: '15 min or less' },
]

const printableCategories = [
  { id: 'all', label: 'All' },
  { id: 'self-regulation', label: 'Self-Regulation' },
  { id: 'life-skills', label: 'Life Skills' },
  { id: 'fine-motor', label: 'Fine Motor' },
  { id: 'yoga-movement', label: 'Yoga & Movement' },
  { id: 'spring-bundle', label: 'Spring Bundle' },
]

const toyAgeFilters = [
  { id: 'all', label: 'All ages' },
  { id: '12-24', label: '1-2 yrs' },
  { id: '24-36', label: '2-3 yrs' },
  { id: '36-48', label: '3-4 yrs' },
  { id: '48-72', label: '4-6 yrs' },
]

const toyDevAreas = [
  { id: 'all', label: 'All' },
  { id: 'fine-motor', label: 'Fine Motor' },
  { id: 'gross-motor', label: 'Gross Motor' },
  { id: 'sensory', label: 'Sensory' },
  { id: 'cognitive', label: 'Cognitive' },
  { id: 'language', label: 'Language' },
  { id: 'social-emotional', label: 'Social-Emotional' },
  { id: 'creative', label: 'Creative' },
]

const devAreaLabels: Record<string, string> = {
  'fine-motor': 'Fine Motor',
  'gross-motor': 'Gross Motor',
  'sensory': 'Sensory',
  'cognitive': 'Cognitive',
  'language': 'Language',
  'social-emotional': 'Social-Emotional',
  'creative': 'Creative',
}

function ageLabel(ageRange: { min: number; max: number }): string {
  const minYears = Math.floor(ageRange.min / 12)
  const maxYears = Math.ceil(ageRange.max / 12)
  return `${minYears}-${maxYears} yrs`
}

const bookAgeFilters = [
  { id: 'all', label: 'All ages' },
  { id: '12-24', label: '1-2 yrs' },
  { id: '24-36', label: '2-3 yrs' },
  { id: '36-48', label: '3-4 yrs' },
  { id: '48-72', label: '4-6 yrs' },
]

const bookCategories = [
  { id: 'all', label: 'All' },
  { id: 'emotions', label: 'Emotions' },
  { id: 'sensory', label: 'Sensory' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'social-skills', label: 'Social Skills' },
  { id: 'bedtime', label: 'Bedtime' },
  { id: 'body-awareness', label: 'Body Awareness' },
  { id: 'indian-culture', label: 'Indian Culture' },
]

type Tab = 'activities' | 'printables' | 'toys' | 'books'

export default function Library() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activities, setActivities] = useState<Activity[]>([])
  const [printables, setPrintables] = useState<Printable[]>([])
  const [toys, setToys] = useState<Toy[]>([])
  const [books, setBooks] = useState<Book[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'activities'
  )

  const selectedCategory = searchParams.get('category') || 'all'
  const selectedTime = searchParams.get('time') || 'all'
  const selectedPrintableCategory = searchParams.get('pcategory') || 'all'
  const selectedToyAge = searchParams.get('toyage') || 'all'
  const selectedToyDev = searchParams.get('toydev') || 'all'
  const selectedBookAge = searchParams.get('bookage') || 'all'
  const selectedBookCategory = searchParams.get('bookcat') || 'all'

  useEffect(() => {
    Promise.all([loadActivities(), loadPrintables(), loadToys(), loadBooks()])
      .then(([acts, prints, toyList, bookList]) => {
        setActivities(acts)
        setPrintables(prints)
        setToys(toyList)
        setBooks(bookList)
      })
      .finally(() => setLoading(false))
  }, [])

  const filteredActivities = activities.filter((activity) => {
    if (selectedCategory !== 'all' && !activity.sensoryTypes.includes(selectedCategory)) {
      return false
    }
    if (selectedTime !== 'all' && activity.duration > parseInt(selectedTime)) {
      return false
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        activity.title.toLowerCase().includes(query) ||
        activity.description.toLowerCase().includes(query)
      )
    }
    return true
  })

  const filteredPrintables = printables.filter((printable) => {
    if (selectedPrintableCategory !== 'all' && printable.category !== selectedPrintableCategory) {
      return false
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        printable.title.toLowerCase().includes(query) ||
        printable.description.toLowerCase().includes(query) ||
        printable.tags.some(t => t.toLowerCase().includes(query))
      )
    }
    return true
  })

  const filteredToys = toys.filter((toy) => {
    if (selectedToyAge !== 'all') {
      const [minStr, maxStr] = selectedToyAge.split('-')
      const filterMin = parseInt(minStr)
      const filterMax = parseInt(maxStr)
      if (toy.ageRange.max < filterMin || toy.ageRange.min > filterMax) return false
    }
    if (selectedToyDev !== 'all' && !toy.developmentAreas.includes(selectedToyDev)) return false
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        toy.name.toLowerCase().includes(query) ||
        toy.description.toLowerCase().includes(query)
      )
    }
    return true
  })

  const filteredBooks = books.filter((book) => {
    if (selectedBookAge !== 'all') {
      const [minStr, maxStr] = selectedBookAge.split('-')
      const filterMin = parseInt(minStr)
      const filterMax = parseInt(maxStr)
      if (book.ageRange.max < filterMin || book.ageRange.min > filterMax) return false
    }
    if (selectedBookCategory !== 'all' && !book.categories.includes(selectedBookCategory)) return false
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        book.title.toLowerCase().includes(query) ||
        book.description.toLowerCase().includes(query) ||
        book.author.toLowerCase().includes(query)
      )
    }
    return true
  })

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value === 'all') {
      params.delete(key)
    } else {
      params.set(key, value)
    }
    setSearchParams(params)
  }

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab)
    setSearchQuery('')
    const params = new URLSearchParams()
    if (tab !== 'activities') params.set('tab', tab)
    setSearchParams(params)
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Library</h1>
        <p className={styles.subtitle}>
          {activeTab === 'activities'
            ? `${activities.length} activities for your child`
            : activeTab === 'printables'
            ? `${printables.length} downloadable printables`
            : activeTab === 'toys'
            ? `${toys.length} science-backed toy picks`
            : `${books.length} recommended reads`}
        </p>
      </header>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'activities' ? styles.tabActive : ''}`}
          onClick={() => handleTabChange('activities')}
        >
          Activities ({activities.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'printables' ? styles.tabActive : ''}`}
          onClick={() => handleTabChange('printables')}
        >
          Printables ({printables.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'toys' ? styles.tabActive : ''}`}
          onClick={() => handleTabChange('toys')}
        >
          Toys ({toys.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'books' ? styles.tabActive : ''}`}
          onClick={() => handleTabChange('books')}
        >
          Books ({books.length})
        </button>
      </div>

      <div className={styles.search}>
        <input
          type="text"
          placeholder={
            activeTab === 'activities' ? 'Search activities...'
            : activeTab === 'printables' ? 'Search printables...'
            : activeTab === 'toys' ? 'Search toys...'
            : 'Search books...'
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {activeTab === 'activities' && (
        <>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Category</label>
              <div className={styles.filterChips}>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`${styles.chip} ${selectedCategory === cat.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('category', cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Time</label>
              <div className={styles.filterChips}>
                {timeFilters.map((time) => (
                  <button
                    key={time.id}
                    className={`${styles.chip} ${selectedTime === time.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('time', time.id)}
                  >
                    {time.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : (
            <div className={styles.activityList}>
              {filteredActivities.length === 0 ? (
                <div className={styles.empty}>
                  <p>No activities match your filters</p>
                  <button
                    className={styles.clearButton}
                    onClick={() => {
                      setSearchParams(new URLSearchParams())
                      setSearchQuery('')
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredActivities.map((activity) => {
                  const situationIcons: Record<string, string> = {
                    wont_sit_still: 'footprints',
                    seeks_crashing: 'zap',
                    focus_difficulty: 'target',
                    picky_eater: 'utensils',
                    meltdowns: 'waves',
                    noise_sensitive: 'ear',
                    sleep_issues: 'moon',
                    avoids_touch: 'hand',
                    transition_difficulty: 'hourglass',
                    clothes_issues: 'shirt',
                  }
                  const hasImage = activity.images && activity.images.length > 0
                  const primarySituation = activity.situationIds[0]

                  return (
                    <button
                      key={activity.id}
                      className={styles.activityCard}
                      onClick={() => navigate(`/activity/${activity.id}`)}
                    >
                      {hasImage ? (
                        <img
                          src={activity.images![0]}
                          alt={activity.title}
                          className={styles.activityThumb}
                        />
                      ) : (
                        <div
                          className={styles.activityThumb}
                          data-situation={primarySituation}
                        >
                          <span className={styles.thumbEmoji}>
                            <Icon name={situationIcons[primarySituation] || 'star'} size={28} />
                          </span>
                        </div>
                      )}
                      <div className={styles.activityInfo}>
                        <div className={styles.activityHeader}>
                          <h3 className={styles.activityTitle}>{activity.title}</h3>
                          {activity.isPremium && (
                            <span className={styles.premiumBadge}>Premium</span>
                          )}
                        </div>
                        <p className={styles.activityDescription}>{activity.description}</p>
                        <div className={styles.activityMeta}>
                          <span>{activity.duration} min</span>
                          <span>·</span>
                          <span>{activity.sensoryTypes.join(', ')}</span>
                        </div>
                      </div>
                      <span className={styles.arrow}>→</span>
                    </button>
                  )
                })
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'printables' && (
        <>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Category</label>
              <div className={styles.filterChips}>
                {printableCategories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`${styles.chip} ${selectedPrintableCategory === cat.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('pcategory', cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : (
            <div className={styles.activityList}>
              {filteredPrintables.length === 0 ? (
                <div className={styles.empty}>
                  <p>No printables match your filters</p>
                  <button
                    className={styles.clearButton}
                    onClick={() => {
                      setSearchParams(new URLSearchParams({ tab: 'printables' }))
                      setSearchQuery('')
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredPrintables.map((printable) => (
                  <button
                    key={printable.id}
                    className={styles.activityCard}
                    onClick={() => navigate(`/printable/${printable.id}`)}
                  >
                    <div className={styles.activityInfo}>
                      <div className={styles.activityHeader}>
                        <h3 className={styles.activityTitle}>{printable.title}</h3>
                        {printable.isPremium ? (
                          <span className={styles.premiumBadge}>Premium</span>
                        ) : (
                          <span className={styles.freeBadge}>Free</span>
                        )}
                      </div>
                      <p className={styles.activityDescription}>{printable.description}</p>
                      <div className={styles.activityMeta}>
                        <span> {printable.pages} pages</span>
                        <span>·</span>
                        <span>{printable.category}</span>
                        {printable.popular && (
                          <>
                            <span>·</span>
                            <span className={styles.popularTag}>Popular</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={styles.arrow}>→</span>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'toys' && (
        <>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Age</label>
              <div className={styles.filterChips}>
                {toyAgeFilters.map((age) => (
                  <button
                    key={age.id}
                    className={`${styles.chip} ${selectedToyAge === age.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('toyage', age.id)}
                  >
                    {age.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Development Area</label>
              <div className={styles.filterChips}>
                {toyDevAreas.map((area) => (
                  <button
                    key={area.id}
                    className={`${styles.chip} ${selectedToyDev === area.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('toydev', area.id)}
                  >
                    {area.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : (
            <div className={styles.activityList}>
              {filteredToys.length === 0 ? (
                <div className={styles.empty}>
                  <p>No toys match your filters</p>
                  <button
                    className={styles.clearButton}
                    onClick={() => {
                      setSearchParams(new URLSearchParams({ tab: 'toys' }))
                      setSearchQuery('')
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredToys.map((toy) => (
                  <a
                    key={toy.id}
                    className={styles.activityCard}
                    href={toy.amazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className={styles.toyThumb}>
                      <span className={styles.thumbEmoji}>{toy.emoji}</span>
                    </div>
                    <div className={styles.activityInfo}>
                      <div className={styles.activityHeader}>
                        <h3 className={styles.activityTitle}>{toy.name}</h3>
                        <span className={styles.ageBadge}>{ageLabel(toy.ageRange)}</span>
                      </div>
                      <p className={styles.activityDescription}>{toy.description}</p>
                      <div className={styles.toyScience}>
                        <span className={styles.scienceIcon}></span>
                        <span>{toy.whyScience}</span>
                      </div>
                      <div className={styles.activityMeta}>
                        {toy.developmentAreas.map((area, i) => (
                          <span key={area}>
                            {i > 0 && ' · '}
                            {devAreaLabels[area] || area}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className={styles.amazonLink}>
                      Amazon →
                    </span>
                  </a>
                ))
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'books' && (
        <>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Age</label>
              <div className={styles.filterChips}>
                {bookAgeFilters.map((age) => (
                  <button
                    key={age.id}
                    className={`${styles.chip} ${selectedBookAge === age.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('bookage', age.id)}
                  >
                    {age.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Category</label>
              <div className={styles.filterChips}>
                {bookCategories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`${styles.chip} ${selectedBookCategory === cat.id ? styles.chipActive : ''}`}
                    onClick={() => setFilter('bookcat', cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : (
            <div className={styles.activityList}>
              {filteredBooks.length === 0 ? (
                <div className={styles.empty}>
                  <p>No books match your filters</p>
                  <button
                    className={styles.clearButton}
                    onClick={() => {
                      setSearchParams(new URLSearchParams({ tab: 'books' }))
                      setSearchQuery('')
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredBooks.map((book) => (
                  <a
                    key={book.id}
                    className={styles.activityCard}
                    href={book.amazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className={styles.bookThumb}>
                      <span className={styles.thumbEmoji}>{book.emoji}</span>
                    </div>
                    <div className={styles.activityInfo}>
                      <div className={styles.activityHeader}>
                        <h3 className={styles.activityTitle}>{book.title}</h3>
                        <span className={styles.ageBadge}>{ageLabel(book.ageRange)}</span>
                      </div>
                      <p className={styles.bookAuthor}>by {book.author}</p>
                      <p className={styles.activityDescription}>{book.description}</p>
                      <div className={styles.toyScience}>
                        <span className={styles.scienceIcon}><Icon name="brain" size={22} /></span>
                        <span>{book.whyRecommended}</span>
                      </div>
                      <div className={styles.activityMeta}>
                        {book.categories.map((cat, i) => (
                          <span key={cat}>
                            {i > 0 && ' · '}
                            {cat.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className={styles.amazonLink}>
                      Amazon →
                    </span>
                  </a>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
